import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, adminAPI, featureFlagsAPI, announcementAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Search,
  Flame,
  Bell,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Minus,
  ExternalLink,
  Loader2,
  ArrowRight,
  AlertTriangle,
  XCircle,
  Shield,
  Crown,
  Megaphone
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, iconBg, iconColor }) => (
  <div className="stat-card animate-fadeIn">
    <div className={`stat-icon ${iconBg}`}>
      <Icon size={24} className={iconColor} />
    </div>
    <div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  </div>
);

const EngagementBadge = ({ level }) => {
  const config = {
    high: { label: 'Cao', className: 'badge-high', icon: TrendingUp },
    medium: { label: 'Trung bình', className: 'badge-medium', icon: Minus },
    low: { label: 'Thấp', className: 'badge-low', icon: TrendingDown },
  };

  const { label, className, icon: BadgeIcon } = config[level] || config.low;

  return (
    <span className={`badge ${className}`}>
      <BadgeIcon size={12} className="mr-1" />
      {label}
    </span>
  );
};

const MarketTrendTech = ({ trend, isEnabled, isVip, vipMessage, maintenanceMessage }) => {
  if (!isEnabled) {
    return (
      <div className="relative overflow-hidden rounded-[2rem] border border-red-500/20 bg-red-500/5 backdrop-blur-2xl p-4 sm:p-6 shadow-xl animate-fadeIn mb-8 group ring-1 ring-red-500/10">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="p-2 sm:p-3 rounded-full bg-red-100 text-red-610 shrink-0">
            <Shield size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-800">Market Trend Đang Bảo Trì</h3>
            <p className="text-red-700/80 text-sm">{maintenanceMessage || "Tính năng này đang được bảo trì để nâng cấp dữ liệu."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!trend) return null;

  const { status, avg_score, analyzed_at } = trend;
  const score = Math.min(100, Math.max(0, Math.round(avg_score)));

  // Determine display values based on VIP status
  const displayScore = isVip ? "??" : score;
  const displayStatus = isVip ? "CHỈ DÀNH CHO VIP" : status;
  const nextCheckTime = trend.info?.next_check_time;
  const assessmentMode = trend.info?.mode;

  const config = {
    'Tốt': {
      color: '#10b981', // emerald-500
      glow: '0 0 20px rgba(16, 185, 129, 0.4)',
      bg: 'rgba(16, 185, 129, 0.1)',
      text: 'Thị trường đang có dấu hiệu tăng trưởng mạnh mẽ với lượng traffic dồi dào. Thuật toán AI phân tích cho thấy tỷ lệ người dùng online và sẵn sàng tương tác đang ở mức Đỉnh điểm.\n🎯 Đề xuất chiến lược:\n• Đây là "Khung giờ vàng", hãy lập tức đẩy mạnh các bài viết quan trọng (Seeding, Bán hàng, Viral).\n• Ưu tiên nội dung Video/Reels hoặc Livestream để tận dụng tối đa độ reach tự nhiên.\n• Phản hồi comment nhanh chóng để đẩy bài viết lên top bảng tin.'
    },
    'Trung bình': {
      color: '#f59e0b', // amber-500
      glow: '0 0 20px rgba(245, 158, 11, 0.4)',
      bg: 'rgba(245, 158, 11, 0.1)',
      text: 'Nhịp độ thị trường hiện tại đang duy trì ở mức Cân bằng. Sự chú ý của người dùng không quá cao nhưng cũng không bị phân tán.\n🎯 Đề xuất chiến lược:\n• Nên đăng tải các bài viết dạng nuôi dưỡng (chia sẻ kiến thức, hình ảnh cá nhân, meme giải trí).\n• Nội dung cần có chất lượng hình ảnh/video cao và caption thu hút ngay 3 giây đầu để giữ chân người xem.\n• Kết hợp chạy tương tác nhẹ bằng các mini-game hoặc câu hỏi thảo luận.'
    },
    'Kém': {
      color: '#ef4444', // red-500
      glow: '0 0 20px rgba(239, 68, 68, 0.4)',
      bg: 'rgba(239, 68, 68, 0.1)',
      text: 'Chỉ số Market Vibe đang ở vùng Thấp. Lưu lượng truy cập thưa thớt và hành vi người dùng hiện đang khá thụ động (chỉ xem, ít tương tác).\n🎯 Đề xuất chiến lược:\n• Tạm thời Lùi lịch đăng các bài viết quan trọng hoặc bài bán hàng.\n• Thay vào đó, đăng tải dạng nội dung ngắn (Story) hoặc tương tác trực tiếp trên bài viết cũ để giữ nhịp.\n• Dành thời gian này để nghiên cứu trending hoặc chuẩn bị sẵn kho nội dung chờ thời điểm thị trường "ấm" lên.'
    }
  };

  const { color, glow, bg, text: statusText } = config[status] || config['Trung bình'];
  
  const displayText = isVip 
    ? (vipMessage || "Thông tin thị trường chuyên sâu và phân tích xu hướng chỉ dành riêng cho thành viên gói VIP. Vui lòng nâng cấp tài khoản để truy cập dữ liệu này.")
    : statusText;
  
  const displayColor = isVip ? '#64748b' : color;
  const displayGlow = isVip ? 'none' : glow;
  const displayBg = isVip ? 'rgba(100, 116, 139, 0.1)' : bg;

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0f172a]/80 backdrop-blur-2xl p-4 sm:p-6 shadow-2xl animate-fadeIn mb-8 group ring-1 ring-white/10">
      {/* Decorative background glow */}
      <div
        className="absolute -right-16 -top-16 w-64 h-64 rounded-full blur-[100px] opacity-10 transition-all duration-1000 group-hover:opacity-20"
        style={{ backgroundColor: color }}
      />

      <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8 relative z-10">
        {/* Circular Gauge */}
        <div className="relative w-28 h-28 sm:w-36 sm:h-36 shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              className="text-white/5"
            />
            {/* Progress circle */}
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke={displayColor}
              strokeWidth="6"
              strokeDasharray={isVip ? "40 264" : `${(score * 2.64).toFixed(1)} 264`}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
              style={{ filter: `drop-shadow(${displayGlow})` }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-4xl font-black text-white ${isVip ? 'blur-sm' : ''}`} style={{ textShadow: displayGlow }}>{displayScore}</span>
            <span className="text-[9px] uppercase font-bold tracking-[0.2em] text-white/40">Power</span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 text-center md:text-left space-y-4">
            <div className="space-y-1">
            <div className="flex items-center justify-center md:justify-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: displayColor, boxShadow: displayGlow }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Viral Potential Index</span>
              {isVip && (
                <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 text-[8px] py-0 px-1.5 h-4">VIP ONLY</Badge>
              )}
            </div>
            <h2 className="text-3xl font-black text-white tracking-tight">
              Tương tác: <span style={{ color: displayColor }} className={isVip ? 'blur-[2px]' : ''}>{displayStatus}</span>
            </h2>
          </div>

          <div className="text-slate-400 text-sm leading-relaxed w-full max-w-2xl bg-white/5 border border-white/10 rounded-2xl p-5 shadow-inner backdrop-blur-sm">
            {isVip ? (
              <p className="italic text-center text-slate-400 py-2">{displayText}</p>
            ) : (
              <div className="space-y-1 text-left">
                 {displayText.split('\n').map((line, i) => {
                    if (line.startsWith('🎯')) {
                       return <div key={i} className="mt-4 mb-2 text-sm font-bold text-white flex items-center gap-2"><Flame size={16} style={{ color: displayColor }}/> {line.substring(2)}</div>;
                    }
                    if (line.startsWith('•')) {
                       return <div key={i} className="flex items-start gap-2.5 mt-1.5"><div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: displayColor, opacity: 0.8 }}/> <span className="text-slate-300 text-sm leading-relaxed flex-1">{line.substring(2)}</span></div>;
                    }
                    if (line.trim() === '') return null;
                    return <p key={i} className="text-slate-200 text-[14px] font-medium leading-relaxed mb-2">{line}</p>;
                 })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center md:justify-start gap-4 mt-2">
            <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/5 flex items-center gap-2">
              <TrendingUp size={14} style={{ color }} />
              <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">AI Analysis active</span>
            </div>
            <span className="text-[10px] text-white/30 font-medium flex flex-col gap-0.5 text-center md:text-left">
              <span>Cập nhật: {new Date(analyzed_at).toLocaleTimeString('vi-VN')}</span>
              {assessmentMode === 'scheduled' && nextCheckTime && (
                <span className="text-blue-400 font-bold">Quét tiếp: {nextCheckTime}</span>
              )}
            </span>
          </div>
        </div>

        {/* Quick Suggestion */}
        <div className="hidden lg:block">
          {isVip ? (
            <Button
              className="h-24 px-6 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 border-none text-white font-black hover:scale-105 transition-transform flex flex-col gap-1 shadow-lg shadow-amber-500/20"
              onClick={() => window.location.href = '/billing'}
            >
              <Crown size={24} />
              <span className="text-xs">NÂNG CẤP VIP</span>
            </Button>
          ) : (
            <div
              className="px-6 py-5 rounded-2xl border transition-all hover:scale-105 shadow-lg flex flex-col items-center gap-2"
              style={{ borderColor: `${color}30`, backgroundColor: bg }}
            >
              <div className="p-2 rounded-xl bg-white/10">
                <Flame size={20} style={{ color }} />
              </div>
              <div className="text-center">
                <span className="block text-[9px] font-bold text-white/50 uppercase tracking-tighter">Recommendation</span>
                <span className="block text-sm font-black text-white mt-0.5">
                  {status === 'Tốt' ? 'ĐĂNG NGAY' : status === 'Trung bình' ? 'TỐI ƯU FB' : 'LÙI LỊCH'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchHealth = async () => {
      try {
        const res = await adminAPI.getSystemHealth();
        setSystemHealth(res.data);
      } catch (error) {
        console.error('Failed to fetch system health:', error);
      }
    };

    const fetchFeatureFlags = async () => {
      try {
        const res = await featureFlagsAPI.getAll();
        setFeatureFlags(res.data);
      } catch (error) {
        console.error('Failed to fetch feature flags:', error);
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const res = await announcementAPI.getActive();
        setAnnouncements(res.data || []);
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
      }
    };

    fetchStats();
    fetchFeatureFlags();
    fetchAnnouncements();
    if (isAdmin) fetchHealth();
  }, [isAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* Welcome Section */}
      <div className="animate-fadeIn">
        <h1 className="text-2xl font-bold text-[#1C1E21]">
          Xin chào, {user?.name}!
        </h1>
        <p className="text-[#606770] mt-1">
          Đây là tổng quan hoạt động của bạn
        </p>
      </div>

      {/* Featured Announcement */}
      {announcements.find(a => a.is_popup) && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl animate-fadeIn relative overflow-hidden group">
          <div className="absolute -right-4 -top-4 opacity-5 group-hover:scale-110 transition-transform duration-500">
            <Megaphone size={100} />
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Megaphone size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none text-[10px] py-0">THÔNG BÁO QUAN TRỌNG</Badge>
                <span className="text-[10px] text-amber-600/60 font-medium">
                  {new Date(announcements.find(a => a.is_popup).created_at).toLocaleDateString('vi-VN')}
                </span>
              </div>
              <h3 className="font-bold text-amber-900 text-lg leading-tight">
                {announcements.find(a => a.is_popup).title}
              </h3>
              <p className="text-amber-800/80 text-sm mt-1 whitespace-pre-wrap leading-relaxed">
                {announcements.find(a => a.is_popup).content}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Admin System Health Alerts */}
      {isAdmin && systemHealth?.warnings?.length > 0 && (
        <div className="space-y-2 animate-fadeIn" data-testid="system-health-alerts">
          {systemHealth.warnings.map((warning, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 p-4 rounded-xl border ${warning.type === 'error'
                ? 'bg-[#FCE9EC] border-[#E41E3F]/20'
                : 'bg-[#FEF9E6] border-[#FFE69C]'
                }`}
              data-testid={`health-warning-${index}`}
            >
              {warning.type === 'error' ? (
                <XCircle size={20} className="text-[#E41E3F] mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle size={20} className="text-[#8F6702] mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-semibold ${warning.type === 'error' ? 'text-[#E41E3F]' : 'text-[#8F6702]'
                  }`}>
                  {warning.category === 'ai_key' ? 'AI Key' : 'Cookie Facebook'}
                </p>
                <p className="text-sm text-[#606770]">{warning.message}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex-shrink-0"
                data-testid={`fix-warning-${index}`}
              >
                <Shield size={14} className="mr-1" />
                Sửa
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Market Trend Tech Section */}
      <MarketTrendTech 
        trend={stats?.market_trend ? { ...stats.market_trend, info: stats.market_trend_info } : null} 
        isEnabled={isAdmin || featureFlags.find(f => f.id === 'market_trend')?.is_enabled !== false}
        isVip={featureFlags.find(f => f.id === 'market_trend')?.is_vip && !isAdmin && user?.subscription_type !== 'monthly'}
        vipMessage={featureFlags.find(f => f.id === 'market_trend')?.vip_message}
        maintenanceMessage={featureFlags.find(f => f.id === 'market_trend')?.message}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Search}
          label="Links đã phân tích"
          value={stats?.total_analyzed || 0}
          iconBg="primary"
          iconColor="text-[#1877F2]"
        />
        <StatCard
          icon={Flame}
          label="Hotface đã lưu"
          value={stats?.total_hotface || 0}
          iconBg="success"
          iconColor="text-[#31A24C]"
        />
        <StatCard
          icon={Bell}
          label="Thông báo đã gửi"
          value={stats?.total_notifications || 0}
          iconBg="warning"
          iconColor="text-[#F5C33B]"
        />
        {(stats?.subscription_type === 'monthly' || isAdmin) && (
          <StatCard
            icon={isAdmin ? Shield : Crown}
            label={isAdmin ? "Quyền Admin" : "Trạng thái VIP"}
            value={isAdmin ? "FULL ACCESS" : "ACTIVE"}
            iconBg={isAdmin ? "primary" : "warning"}
            iconColor={isAdmin ? "text-blue-500" : "text-amber-500"}
          />
        )}
      </div>

      {isAdmin && stats?.subscription_type !== 'monthly' && (
        <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl text-white shadow-lg animate-fadeIn relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Shield size={80} />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-white/20 hover:bg-white/30 text-white border-none">ADMINISTRATOR</Badge>
                <span className="text-sm font-medium opacity-80 text-blue-100">Toàn quyền sử dụng hệ thống</span>
              </div>
              <h3 className="text-xl font-bold">Bạn đang truy cập với quyền Quản trị viên!</h3>
              <p className="text-sm text-blue-100 mt-1">
                Tài khoản của bạn được phép sử dụng tất cả tính năng mà không cần nâng cấp VIP.
              </p>
            </div>
            <Button 
              variant="outline" 
              className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
              onClick={() => navigate('/admin')}
            >
              Quản lý hệ thống
            </Button>
          </div>
        </div>
      )}

      {stats?.subscription_type === 'monthly' && (
        <div className="p-4 bg-gradient-to-r from-[#1877F2] to-[#3B5998] rounded-2xl text-white shadow-lg animate-fadeIn relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Shield size={80} />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-white/20 hover:bg-white/30 text-white border-none">PREMIUM PLAN</Badge>
                <span className="text-sm font-medium opacity-80">Hết hạn: {new Date(stats?.subscription_expiry).toLocaleDateString('vi-VN')}</span>
              </div>
              <h3 className="text-xl font-bold">Bạn đang trong thời hạn gói tháng!</h3>
            </div>
            {stats?.subscription_expiry && (new Date(stats.subscription_expiry) - new Date()) / (1000 * 60 * 60 * 24) <= 3 && (
              <Button 
                variant="outline" 
                className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
                onClick={() => navigate('/billing')}
              >
                Gia hạn ngay
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Engagement Breakdown & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Engagement Breakdown */}
        <Card className="lg:col-span-2 animate-fadeIn stagger-1" data-testid="engagement-breakdown">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Phân loại tương tác</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-[#EBF5ED] rounded-xl">
                <TrendingUp size={32} className="mx-auto text-[#31A24C] mb-2" />
                <div className="text-3xl font-bold text-[#31A24C]">
                  {stats?.engagement_breakdown?.high || 0}
                </div>
                <div className="text-sm text-[#606770] mt-1">Tương tác cao</div>
              </div>
              <div className="text-center p-4 bg-[#FEF9E6] rounded-xl">
                <Minus size={32} className="mx-auto text-[#8F6702] mb-2" />
                <div className="text-3xl font-bold text-[#8F6702]">
                  {stats?.engagement_breakdown?.medium || 0}
                </div>
                <div className="text-sm text-[#606770] mt-1">Tương tác TB</div>
              </div>
              <div className="text-center p-4 bg-[#FCE9EC] rounded-xl">
                <TrendingDown size={32} className="mx-auto text-[#E41E3F] mb-2" />
                <div className="text-3xl font-bold text-[#E41E3F]">
                  {stats?.engagement_breakdown?.low || 0}
                </div>
                <div className="text-sm text-[#606770] mt-1">Tương tác thấp</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="animate-fadeIn stagger-2" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Thao tác nhanh</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full justify-between bg-[#1877F2] hover:bg-[#166FE5]"
              onClick={() => navigate('/analyze')}
              data-testid="quick-analyze-btn"
            >
              <span className="flex items-center gap-2">
                <Search size={18} />
                Phân tích Link mới
              </span>
              <ArrowRight size={18} />
            </Button>
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => navigate('/hotface')}
              data-testid="quick-hotface-btn"
            >
              <span className="flex items-center gap-2">
                <Flame size={18} />
                Xem Hotface List
              </span>
              <ArrowRight size={18} />
            </Button>
            {!isAdmin && (
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => navigate('/billing')}
                data-testid="quick-billing-btn"
              >
                <span className="flex items-center gap-2">
                  <Crown size={18} className="text-amber-500" />
                  Gia hạn / Nâng cấp VIP
                </span>
                <ArrowRight size={18} />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Links */}
      <Card className="animate-fadeIn stagger-3" data-testid="recent-links">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Links phân tích gần đây</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/analyze')}
            className="text-[#1877F2]"
          >
            Xem tất cả
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_links?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_links.map((link, index) => (
                <div
                  key={link.id}
                  className="flex items-center justify-between p-3 bg-[#F7F8FA] rounded-lg hover:bg-[#F0F2F5] transition-colors"
                >
                  <div className="flex-1 min-w-0 mr-4">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="url-text hover:underline flex items-center gap-1.5 font-medium overflow-hidden text-[#1877F2]"
                    >
                      <span className="truncate flex-1">
                        {link.page_name ? `${link.page_name} - ` : ''}{link.url.substring(0, 50)}...
                      </span>
                      <ExternalLink size={12} className="shrink-0" />
                    </a>
                    <p className="text-xs text-[#8C939D] mt-1">
                      {new Date(link.analyzed_at).toLocaleDateString('vi-VN')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#1C1E21]">
                        {link.engagement_score}
                      </p>
                      <p className="text-xs text-[#8C939D]">điểm</p>
                    </div>
                    <EngagementBadge level={link.engagement_level} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Search size={48} className="empty-state-icon mx-auto" />
              <h3 className="empty-state-title">Chưa có link nào</h3>
              <p className="empty-state-text">
                Bắt đầu phân tích link Facebook đầu tiên của bạn
              </p>
              <Button
                onClick={() => navigate('/analyze')}
                className="bg-[#1877F2] hover:bg-[#166FE5]"
              >
                Phân tích ngay
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
